import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { convertApiHeaderToReactTableHeader } from "src/components/Common/UI/DndTable/UtilFunctions";
import {SydFilterContext} from "src/components/Common/UI/SydFilter/SydFilterContext";
import { useGetBillingSummaryReport } from "../api/getBillingSummaryReport";
import { format } from 'date-fns';
import { useBillingSummaryDownloadCsv } from "../api/getBillingSummaryDownloadCsv";
import { summaryDetailsColumnData, summaryLinesColumnData } from "../data/BillingSummaryColumns";
import { useGetBillingSummaryDownloadExcel } from "../api/getBillingSummaryDownloadExcel";


export const useBillingSummaryDataViewLogic = () => {
  const { t } = useTranslation();
  const { activeFilters, isFilterSubmitedOnce } = useContext(SydFilterContext);
  const [modalEvent, setModalEvent] = useState<IEvent>({action: "", selectedValue: ""});
  const [selectedRowsOfTable, setSelectedRowsOfTable] = useState<IGetBillingSummaryReport["linesData"]>([]);
  const downloadAnchorRef = useRef<HTMLAnchorElement>(null);
  const billingSummaryDownloadCsvMutation = useBillingSummaryDownloadCsv({data: activeFilters as IBillingSummarySearchForm});
  const billingSummaryDownloadExcelMutation = useGetBillingSummaryDownloadExcel({data: activeFilters as IBillingSummarySearchForm});
  const getBillingSummaryReportMutation = useGetBillingSummaryReport({
    data: activeFilters as IBillingSummarySearchForm
  });

  const dataTypes = [
    { id: 0, apiType: "Default", frontType: "", frontDisplayFormat: "" },
    { id: 1, apiType: "Integer", frontType: "number", frontDisplayFormat: "", defaultEmptyValue: 0 },
    { id: 2, apiType: "Double", frontType: "number", frontDisplayFormat: "", defaultEmptyValue: 0 },
    { id: 3, apiType: "Date", frontType: "date", frontDisplayFormat: "YYYY-MM-DD HH:mm:ss", sourceDisplayFormat: "YYYY-MM-DDTHH:mm:ss" },
    { id: 4, apiType: "String", frontType: "string", frontDisplayFormat: "" },
    { id: 5, apiType: "yyyyMMdd", frontType: "date", frontDisplayFormat: "YYYY-MM-DD", sourceDisplayFormat: "YYYYMMDD" },
    { id: 6, apiType: "Boolean", frontType: "boolean", frontDisplayFormat: "" },
    { id: 7, apiType: "IntegerTime", frontType: "date", frontDisplayFormat: "HH:mm:ss", sourceDisplayFormat: "seconds" },
    { id: 8, apiType: "yyyyMM", frontType: "date", frontDisplayFormat: "MMM YYYY", sourceDisplayFormat: "YYYYMM" }
  ];

  let canDownloadCsv = false;
  let canDownloadExcel = false;
  let summaryLinesfooterVisibility = false;

  const loadingDone = !getBillingSummaryReportMutation.isLoading
  const summaryDetailsData = getBillingSummaryReportMutation.data?.detailsData ? getBillingSummaryReportMutation.data.detailsData : [];
  const summaryLinesData = getBillingSummaryReportMutation.data?.linesData ? getBillingSummaryReportMutation.data.linesData : [];
  const countLinesData = getBillingSummaryReportMutation.data?.linesData ? getBillingSummaryReportMutation.data.linesData.length : 0;

  if(getBillingSummaryReportMutation.data){
    canDownloadCsv = getBillingSummaryReportMutation.data?.linesData.length > 0;
    canDownloadExcel = getBillingSummaryReportMutation.data?.linesData.length > 0;
    summaryLinesfooterVisibility = getBillingSummaryReportMutation.data?.linesData.length > 0;
  }

  const summaryDetailsColumns = useMemo(() => {
    const filteredColumns = summaryDetailsColumnData.filter(column => {
      const detailsData = getBillingSummaryReportMutation.data?.detailsData[0] as IGetBillingSummaryDetails | undefined;
      
      if (column.columnName.startsWith("fpa")) {
        return detailsData?.hasOwnProperty(column.columnName) && detailsData[column.columnName as keyof IGetBillingSummaryDetails] > 0;
      } else {
        return true;
      }
    });
    
    return convertApiHeaderToReactTableHeader(
      filteredColumns,
      dataTypes
    );
  }, [getBillingSummaryReportMutation.data?.detailsData]);

  const summaryLinesColumns = useMemo(() => {
    const columnsWithFooter = summaryLinesColumnData(getBillingSummaryReportMutation.data?.linesDataFooter)
    return convertApiHeaderToReactTableHeader(
      columnsWithFooter,
      dataTypes
    );
  }, [getBillingSummaryReportMutation.data?.linesDataFooter]);

  const closeActionModal = () => {
    setModalEvent({ action: "", selectedRows: [] });
  };

  const getModalTitleByAction = (): string => {
    let title = "";
    switch (modalEvent.action) {
      case "create":
        title = t("Δημιουργία Μονάδας");
        break;
      case "edit":
        title = t("Επεξεργασία Μονάδας");
        break;
      case "delete":
        title = t("Διαγραφή Μονάδας");
        break;
      case "showDetails":
        title = t("Συγκεντροτική Οθόνη Μονάδας");
        break;  
      default:
        break;
    }
    return title;
  };

  const downloadFile = (blobString: string, reportName: string, fileExtension: string) => {
    if (downloadAnchorRef.current) {
      const recordingsBlob = new Blob([blobString], { type: 'application/octet-stream' }); // Set the type to 'application/octet-stream'
      const recordingsBlobURL = URL.createObjectURL(recordingsBlob);

      const currentDate: Date = new Date();
      const nowDateFormated: string = format(currentDate, 'yyyy-MM-dd_HH-mm-ss');
      const fileName:string = `${reportName}_${nowDateFormated}.${fileExtension}`;

      downloadAnchorRef.current.href = recordingsBlobURL;
      downloadAnchorRef.current.download = fileName;
      downloadAnchorRef.current.click();
    }
  }

  const DownloadCsv = async () => {
    billingSummaryDownloadCsvMutation.mutateAsync({data: activeFilters as IBillingSummarySearchForm})
    .then((apiRecordingsCsvResponse) => {
      downloadFile(apiRecordingsCsvResponse, "BillingSummary", "csv");
    }).catch((error) => {
      console.log(error)
    }) 
  }

  const DownloadExcel = async () => {
    billingSummaryDownloadExcelMutation.mutateAsync({data: activeFilters as IBillingSummarySearchForm})
    .then((apiRecordingsExcelResponse) => {
      downloadFile(apiRecordingsExcelResponse, "BillingSummary", "xlsx");
    }).catch((error) => {
      console.log(error)
    })
  }

  const actionEvent = async (event: IEvent) => {
    switch (event.action) {
      case "downloadCsv":
        await DownloadCsv();
        break;
      case "downloadExcel":
        await DownloadExcel();
        break;
      default:
        setModalEvent({ action: event.action, selectedValue: selectedRowsOfTable[0].phoneNumber });
    }
  };

  useEffect(() => {
    setSelectedRowsOfTable([]);

    getBillingSummaryReportMutation.mutateAsync(
      {data: activeFilters as IBillingSummarySearchForm}
    ).catch((error) => {
      console.log(error);
    });

  }, [activeFilters]);

  return {
    summaryDetailsColumns,
    summaryDetailsData,
    summaryLinesColumns,
    summaryLinesData,
    loadingDone,
    selectedRowsOfTable,
    setSelectedRowsOfTable,
    t,
    modalEvent,
    actionEvent,
    closeActionModal,
    getModalTitleByAction,
    countLinesData,
    downloadAnchorRef,
    canDownloadCsv,
    canDownloadExcel,
    summaryLinesfooterVisibility
  };
};
